import { useState, useRef, useEffect } from "react";
import useWindowWidth from "../../../hooks/useWindowWidth";
import { Link } from "react-router-dom";
import { FaEye, FaCamera, FaTimes, FaPlay } from "react-icons/fa";
import "../../../styles/components/AllProjects/ProjectCard.css";
import { useLanguage } from "../../../hooks/LanguageContext";

function ProjectCard({
  title,
  description,
  image,
  technologies,
  link,
  projectImages,
}) {
  const { isEnglish } = useLanguage();
  const descriptionText = isEnglish ? description.en : description.esp;
  const [isFlipped, setIsFlipped] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const isMobile = useWindowWidth();
  const scrollContainerRef = useRef(null);
  const hasGallery = Array.isArray(projectImages) && projectImages.length > 0;

  const handleInteraction = () => {
    if (isMobile) {
      setIsFlipped((prev) => !prev);
    }
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    setShowImages(true);
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !showImages) return undefined;

    let isDown = false;
    let startX;
    let scrollLeft;

    const handleMouseDown = (e) => {
      isDown = true;
      startX = e.pageX - scrollContainer.offsetLeft;
      scrollLeft = scrollContainer.scrollLeft;
      scrollContainer.style.cursor = "grabbing";
    };

    const handleMouseLeave = () => {
      isDown = false;
      scrollContainer.style.cursor = "grab";
    };

    const handleMouseUp = () => {
      isDown = false;
      scrollContainer.style.cursor = "grab";
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - scrollContainer.offsetLeft;
      const walk = (x - startX) * 2;
      scrollContainer.scrollLeft = scrollLeft - walk;
    };

    scrollContainer.addEventListener("mousedown", handleMouseDown);
    scrollContainer.addEventListener("mouseleave", handleMouseLeave);
    scrollContainer.addEventListener("mouseup", handleMouseUp);
    scrollContainer.addEventListener("mousemove", handleMouseMove);

    return () => {
      scrollContainer.removeEventListener("mousedown", handleMouseDown);
      scrollContainer.removeEventListener("mouseleave", handleMouseLeave);
      scrollContainer.removeEventListener("mouseup", handleMouseUp);
      scrollContainer.removeEventListener("mousemove", handleMouseMove);
    };
  }, [showImages]);

  useEffect(() => {
    if (!showImages) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setShowImages(false);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "auto";
    };
  }, [showImages]);

  const isHttpLink = /^https?:\/\//i.test(link);
  const isFinanceApp = link === "/finances" || link.includes("finances-app");

  return (
    <div className="card-container">
      <div
        className={`card ${isFlipped ? "flipped" : ""}`}
        onClick={handleInteraction}
        role={isMobile ? "button" : undefined}
        tabIndex={isMobile ? 0 : undefined}
        onKeyDown={
          isMobile
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleInteraction();
                }
              }
            : undefined
        }
      >
        <div className="content">
          <div className="front">
            <div className="front-content">
              <img src={image} alt={title} className="card-image" />
              <h3 className="card-title">{title}</h3>
              <div className="card-technologies">
                {technologies.map((tech) => (
                  <span key={tech} className="tech-badge">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="back">
            <div className="back-content">
              <h3 className="card-title">{title}</h3>
              <p className="card-description">{descriptionText}</p>
              <div className="card-technologies">
                {technologies.map((tech) => (
                  <span key={`back-${tech}`} className="tech-badge">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-buttons">
        {/* Finance App is built with base path /finances — must open via same-origin rewrite */}
        {isFinanceApp ? (
          <a
            href="/finances"
            target="_self"
            className="link-button"
            aria-label={isEnglish ? `Open ${title}` : `Abrir ${title}`}
            onClick={(e) => e.stopPropagation()}
          >
            <FaPlay />
          </a>
        ) : isHttpLink ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="link-button"
            aria-label={isEnglish ? `Open ${title}` : `Abrir ${title}`}
            onClick={(e) => e.stopPropagation()}
          >
            <FaEye />
          </a>
        ) : (
          <Link
            to={link}
            className="link-button"
            aria-label={isEnglish ? `Open ${title}` : `Abrir ${title}`}
            onClick={(e) => e.stopPropagation()}
          >
            <FaPlay />
          </Link>
        )}

        {hasGallery ? (
          <button
            type="button"
            onClick={handleImageClick}
            className="photos-button"
            aria-label={isEnglish ? "View screenshots" : "Ver capturas"}
          >
            <FaCamera />
          </button>
        ) : null}
      </div>

      {showImages && (
        <div
          className="image-modal"
          onClick={() => setShowImages(false)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="image-modal-close"
            onClick={() => setShowImages(false)}
            aria-label={isEnglish ? "Close" : "Cerrar"}
          >
            <FaTimes />
          </button>
          <div
            className="image-modal-content"
            ref={scrollContainerRef}
            onClick={(e) => e.stopPropagation()}
          >
            {projectImages.map((img, index) => (
              <img
                key={img}
                src={img}
                alt={`${title} — ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectCard;
